import { ImpersonationBanner } from '@/components/admin/ImpersonationBanner';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/hooks/useAuth';
import { useSignOut } from '@/hooks/useSignOut';
import { cn } from '@/lib/utils';
import {
  CreditCard,
  Database,
  KeyRound,
  LayoutDashboard,
  LifeBuoy,
  Lightbulb,
  LineChart,
  LogOut,
  MessageSquare,
  Network,
  Settings as SettingsIcon,
  ShieldCheck,
  User as UserIcon,
  Users as UsersIcon,
} from 'lucide-react';
import { Link, NavLink, Outlet } from 'react-router-dom';

const navItems = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/discoveries', label: 'Discoveries', icon: Network },
  { to: '/datasets', label: 'Datasets', icon: Database },
  { to: '/knowledge', label: 'Knowledge', icon: Lightbulb },
  { to: '/api-keys', label: 'API Keys', icon: KeyRound },
  { to: '/billing', label: 'Billing', icon: CreditCard },
  { to: '/support', label: 'Support', icon: LifeBuoy },
  { to: '/settings', label: 'Settings', icon: SettingsIcon },
];

// Superuser-only. Rendered as a separate section so it's visually distinct
// from a tenant's own product nav and never shown to regular members.
const adminNavItems = [
  { to: '/admin/overview', label: 'Overview', icon: ShieldCheck },
  { to: '/admin/traffic', label: 'Traffic', icon: LineChart },
  { to: '/admin/users', label: 'Users', icon: UsersIcon },
  { to: '/admin/tickets', label: 'Tickets', icon: MessageSquare },
];

function initialsFor(user: { display_name?: string | null; email: string } | null): string {
  if (!user) return '';
  const source = user.display_name?.trim() || user.email;
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() ?? '');
  return letters.join('') || '?';
}

export function AppLayout() {
  const { user, org, isSuperuser } = useAuth();
  const signOut = useSignOut();
  const initials = initialsFor(user);

  return (
    <div className="flex h-screen w-full flex-col bg-background">
      <ImpersonationBanner />
      <div className="flex min-h-0 w-full flex-1">
        <aside className="hidden w-60 shrink-0 flex-col border-r bg-sidebar text-sidebar-foreground md:flex">
          <div className="flex h-14 items-center gap-2 border-b px-4 font-semibold">
            <Network className="h-5 w-5" />
            <span>DAGsight</span>
          </div>
          <nav className="flex-1 space-y-1 p-2">
            {navItems.map(({ to, label, icon: Icon }) => (
              <NavLink
                key={to}
                to={to}
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                      : 'hover:bg-sidebar-accent/60',
                  )
                }
              >
                <Icon className="h-4 w-4" />
                {label}
              </NavLink>
            ))}

            {isSuperuser && (
              <>
                <div className="px-3 pb-1 pt-4 text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  Admin
                </div>
                {adminNavItems.map(({ to, label, icon: Icon }) => (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                          : 'hover:bg-sidebar-accent/60',
                      )
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {label}
                  </NavLink>
                ))}
              </>
            )}
          </nav>
          <Separator />
          <div className="p-3 text-xs text-muted-foreground">v0.1.0</div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex h-14 shrink-0 items-center justify-between border-b px-4">
            <div className="text-sm text-muted-foreground">{org ? org.name : 'DAGsight'}</div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-9 gap-2 px-2">
                  <Avatar className="h-7 w-7">
                    <AvatarFallback>{initials || <UserIcon className="h-4 w-4" />}</AvatarFallback>
                  </Avatar>
                  <span className="hidden text-sm sm:inline">
                    {user?.display_name || user?.email || 'Account'}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel className="flex flex-col">
                  <span className="text-sm font-medium">{user?.display_name || 'My Account'}</span>
                  {user?.email ? (
                    <span className="truncate text-xs font-normal text-muted-foreground">
                      {user.email}
                    </span>
                  ) : null}
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/settings">
                    <SettingsIcon className="mr-2 h-4 w-4" /> Settings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={(e) => {
                    e.preventDefault();
                    if (!signOut.isPending) signOut.mutate();
                  }}
                  disabled={signOut.isPending}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {signOut.isPending ? 'Signing out…' : 'Sign out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </header>
          <main className="flex-1 overflow-auto p-6">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
